import axios from "axios";
import { toast } from "sonner";

export const ALPHA_BLOCK_NOTE = "Action blocked — target is an Alpha.";

// Derive the backend origin at runtime from the current page origin so every API/WS
// call stays same-origin regardless of which hostname the app was opened on.
const BACKEND_URL = window.location.origin;
export const API_BASE = `${BACKEND_URL}/api`;
export const LOGO = process.env.REACT_APP_LOGO_URL;
export const AUTH_BG = process.env.REACT_APP_AUTH_BG_URL;

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// Collapse identical concurrent GETs (presence, unread counts, notifications…) into one request.
// Also reuse a just-finished GET for a brief window (cleared by any write) to absorb near-simultaneous mounts.
const _inflight = new Map();
const _recent = new Map();
const RECENT_MS = 1500;
const _primed = new Map();
const PRIME_MS = 4000;
const _rawGet = api.get.bind(api);
api.get = (url, config) => {
  if (config?.responseType && config.responseType !== "json") return _rawGet(url, config);
  const key = `${url}|${config?.params ? JSON.stringify(config.params) : ""}`;
  const primed = _primed.get(key);
  if (primed) {
    if (Date.now() - primed.at < PRIME_MS) return primed.p.then((data) => (data == null ? _rawGet(url, config) : { data }));
    _primed.delete(key);
  }
  const recent = _recent.get(key);
  if (recent && Date.now() - recent.at < RECENT_MS) return recent.p;
  const hit = _inflight.get(key);
  if (hit) return hit;
  const p = _rawGet(url, config).finally(() => _inflight.delete(key));
  _inflight.set(key, p);
  p.then(() => _recent.set(key, { p, at: Date.now() }), () => {});
  return p;
};

// Fetch one bundle endpoint and serve each part to the first matching api.get(url) — panels stay unchanged.
const _bundles = new Map();
export function primeBundle(bundleUrl, parts) {
  const prev = _bundles.get(bundleUrl);
  if (prev && Date.now() - prev.at < PRIME_MS) return prev.bundle;
  const bundle = _rawGet(bundleUrl).then((r) => r.data || {}, () => ({}));
  const at = Date.now();
  _bundles.set(bundleUrl, { bundle, at });
  Object.entries(parts).forEach(([url, field]) => _primed.set(`${url}|`, { p: bundle.then((b) => b[field]), at }));
  return bundle;
}

const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

function readCookie(name) {
  const prefix = `${name}=`;
  const item = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
}

// Double-submit CSRF: echo the readable csrf_token cookie on state-changing requests.
// Self-heal: if the readable csrf_token cookie is missing (stale/older session, or a
// write fires before /auth/me has run), fetch /auth/me — which re-sets the cookie — then
// attach it. A response guard retries a write ONCE if the server still reports a CSRF miss.
async function ensureCsrfToken() {
  let token = readCookie("csrf_token");
  if (token) return token;
  try {
    await api.get("/auth/me");
  } catch (e) {
    /* not logged in / offline — the write will fail as expected */
  }
  return readCookie("csrf_token");
}

api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (UNSAFE_METHODS.has(method)) {
    _recent.clear();
    _primed.clear();
    _bundles.clear();
    const token = readCookie("csrf_token") || (await ensureCsrfToken());
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// Fire a one-shot "meaningful action" signal after the user's first post / message / den-join,
// so the push-permission nudge appears right after a genuine engagement (not on a blind timer).
const MEANINGFUL_POST = [
  /\/posts$/,                         // publish a howl
  /\/messages$/,                      // send a DM
  /\/group-dms\/[^/]+\/messages$/,    // send a group DM
  /\/rooms\/[^/]+\/messages$/,        // send a den message
  /\/rooms\/[^/]+\/join$/,            // join a den
];
let meaningfulFired = false;
function maybeSignalMeaningful(config) {
  if (meaningfulFired || typeof window === "undefined") return;
  if ((config?.method || "").toLowerCase() !== "post") return;
  const url = (config?.url || "").split("?")[0];
  if (MEANINGFUL_POST.some((re) => re.test(url))) {
    meaningfulFired = true;
    window.dispatchEvent(new Event("pack:meaningful-action"));
  }
}

// Schema guard: a proxy/HTML error page or a malformed document must never reach a component.
function sanitizeList(arr) {
  return arr.filter((x) => x !== null && x !== undefined && (typeof x !== "object" || Array.isArray(x) || Object.keys(x).length > 0));
}
function guardResponse(r) {
  const ct = String(r.headers?.["content-type"] || "");
  if (r.config?.responseType && r.config.responseType !== "json") return r;
  if (typeof r.data === "string" && ct.includes("text/html")) {
    const err = new Error("Unexpected response from the sanctuary");
    err.response = { ...r, status: 502, data: { detail: "The sanctuary sent back something unexpected — please try again." } };
    err.config = r.config;
    throw err;
  }
  const d = r.data;
  if (Array.isArray(d)) r.data = sanitizeList(d);
  else if (d && typeof d === "object") {
    for (const k of Object.keys(d)) if (Array.isArray(d[k])) d[k] = sanitizeList(d[k]);
  }
  return r;
}

// Optional CDN: when REACT_APP_CDN_BASE_URL is set, signed (self-authorising) file URLs are served from the CDN.
const CDN_BASE = (process.env.REACT_APP_CDN_BASE_URL || "").replace(/\/$/, "");
const CDN_RE = /^\/api\/files\/(s|t)\//;
function cdnRewrite(v, depth = 0) {
  if (typeof v === "string") return CDN_RE.test(v) ? CDN_BASE + v : v;
  if (!v || typeof v !== "object" || depth > 8) return v;
  if (Array.isArray(v)) { for (let i = 0; i < v.length; i++) v[i] = cdnRewrite(v[i], depth + 1); return v; }
  for (const k of Object.keys(v)) v[k] = cdnRewrite(v[k], depth + 1);
  return v;
}

api.interceptors.response.use(
  (r) => {
    maybeSignalMeaningful(r.config);
    const out = guardResponse(r);
    if (CDN_BASE && out?.data && typeof out.data === "object" && !(out.data instanceof Blob)) cdnRewrite(out.data);
    return out;
  },
  async (error) => {
    const { response, config } = error || {};
    // Validation errors (422) arrive as a Pydantic array — flatten once here so every
    // `toast.error(detail)` call site gets readable text instead of crashing React.
    if (response?.data && Array.isArray(response.data.detail)) {
      response.data.detail = formatApiErrorDetail(response.data.detail);
    }
    const detail = response?.data?.detail;

    // Rate-limit warnings removed for posts, comments, DMs, group DMs and den messages — these
    // actions carry no frequency restriction. A 429 (only possible from infra-level abuse guards)
    // is rejected quietly, with no user-facing popup or toast.
    if (response?.status === 429) {
      return Promise.reject(error);
    }

    // Alpha protection: one consistent toast for every warn/ban/kick/mute aimed at an Alpha.
    if (response?.status === 403 && detail === ALPHA_BLOCK_NOTE) {
      toast.error(ALPHA_BLOCK_NOTE, { id: "alpha-guard" });
      error.alphaBlocked = true;
      return Promise.reject(error);
    }

    // Session revoked (e.g. an Alpha force-logout, or "sign out everywhere"): any 401 on a
    // normal API call means this client's token is no longer valid. Bounce to the login screen
    // so the redirect is consistent across the feed, DMs, group DMs and dens. Auth-bootstrap
    // calls (/auth/*) are exempt — AuthContext handles those (a logged-out visitor isn't redirected).
    const reqUrl = config?.url || "";
    const isAuthRoute = reqUrl.includes("/auth/");
    if (response?.status === 401 && !isAuthRoute && typeof window !== "undefined") {
      if (!window.location.pathname.startsWith("/enter")) {
        window.location.href = "/enter";
      }
      return Promise.reject(error);
    }

    if (
      response?.status === 403 &&
      detail === "CSRF validation failed" &&
      config &&
      !config._csrfRetried
    ) {
      config._csrfRetried = true;
      const token = await ensureCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers["X-CSRF-Token"] = token;
        return api(config);
      }
    }

    // Item 7 — retry on momentary network/server blips (incl. Cloudflare 520-523 while a redeploy's new
    // pod is still booting). GETs always; writes only when the caller marks them `idempotent: true`
    // (safe to repeat — e.g. quest claims). Network errors (no response) or transient gateway codes.
    const method = (config?.method || "get").toLowerCase();
    const GATEWAY = [502, 503, 504, 520, 521, 522, 523, 524];
    const isTransient = !response || GATEWAY.includes(response.status);
    const retryable = method === "get" || config?.idempotent === true;
    if (config && retryable && isTransient && config._retryCount !== -1) {
      config._retryCount = config._retryCount || 0;
      const max = method === "get" ? 2 : 3;
      if (config._retryCount < max) {
        const delay = (method === "get" ? 400 : 1000) * Math.pow(2, config._retryCount);
        config._retryCount += 1;
        await new Promise((r) => setTimeout(r, delay));
        return api(config);
      }
    }
    // Never surface raw proxy/CDN error text (e.g. "The origin web server sent a response that Cloudflare could not parse").
    if (response && GATEWAY.includes(response.status)) {
      response.data = { ...(typeof response.data === "object" && response.data ? response.data : {}),
        detail: "The sanctuary is waking up after an update — please try again in a few seconds." };
    }

    return Promise.reject(error);
  }
);

export function fileUrl(path, width) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const clean = path.startsWith("/api/") ? path.slice(4) : path;
  const url = `${API_BASE}${clean}`;
  return width ? `${url}?w=${width}` : url;
}

export function wsUrl() {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws`;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
